import { useNavigate } from "../lib/router-context";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../components/LanguageSelector";
import { AccountLink } from "../components/AccountLink";
import { AdminLink } from "../components/AdminLink";
import { TabButton, TabGroup } from "../components/ui/Tabs";
import { Leaderboard } from "../components/Leaderboard";
import { Timeline } from "../components/Timeline";
import { api, type ScoreEntry } from "../lib/api";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";

import { ThemeToggle } from "../components/ThemeToggle";
import { HomeLink } from "../components/HomeLink";
import { DiscordButton } from "../components/DiscordButton";
import { trackEvent } from "../lib/analytics";
import { useOpenLobbyStore } from "../lib/games/openlobby/store";
import { useOpenLobbySSE } from "../lib/useOpenLobbySSE";
import { getRequestedGameLanguage } from "../lib/wikipediaApi";
import {
  BoardHero,
  type BoardOpenLobby,
} from "../components/board/BoardHero";
import { ModeRulesCard } from "../components/board/ModeRulesCard";
import { Pawn } from "../components/board/Pawn";
import { GameLoadingScreen } from "../components/GameLoadingScreen";
import { SvgIcon } from "../components/ui/SvgIcon";
import mapIcon from "../assets/map-icon.svg?raw";
import { features } from "../lib/features";

const RULE_STEPS = [0, 1, 2, 3, 4, 5] as const;

interface LeaderboardDisplayEntry {
  name: string;
  time: number;
  clicks: number;
  meta?: string;
}

export function IndexPage() {
  const navigate = useNavigate();
  const [lobbyCode, setLobbyCode] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const activeDate = useAppStore((state) => state.activeDate);
  const setActiveDate = useAppStore((state) => state.setActiveDate);
  const [activeView, setActiveView] = useState<"leaderboard" | "paths">(
    "leaderboard",
  );
  const [leaderboard, setLeaderboard] = useState<LeaderboardDisplayEntry[]>([]);
  const [scoreboard, setScoreboard] = useState<ScoreEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [streamer, setStreamer] = useState<string | null>(null);
  const lang = useAppStore((state) => state.lang);
  const gameLanguage = getRequestedGameLanguage(lang);
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();
  const footerLinkClassName = `${theme.textMuted} ${isDark ? "hover:text-white" : "hover:text-gray-900"} transition-colors`;

  useEffect(() => {
    let isCancelled = false;

    api
      .getStreamer()
      .then((channel) => {
        if (!isCancelled) setStreamer(channel);
      })
      .catch(() => {
        if (!isCancelled) setStreamer(null);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  const { connected: openLobbyConnected } = useOpenLobbySSE(
    gameLanguage,
    features.openLobby,
  );
  const openLobbyState = useOpenLobbyStore((state) => state.gameState);
  const [gameRemaining, setGameRemaining] = useState<number | null>(null);
  const [scoreCountdown, setScoreCountdown] = useState<number | null>(null);

  const boardOpenLobby = useMemo<BoardOpenLobby | null>(
    () =>
      features.openLobby && openLobbyState
        ? {
            startTitle: openLobbyState.start.title,
            targetTitle: openLobbyState.target_article.title,
            phase: openLobbyState.phase,
            playerCount: Object.keys(openLobbyState.players).length,
            gameRemaining,
            scoreCountdown,
          }
        : null,
    [openLobbyState, gameRemaining, scoreCountdown],
  );

  useEffect(() => {
    if (!openLobbyState?.start_time) return;
    const startMs = new Date(openLobbyState.start_time).getTime();
    const scoreDuration = openLobbyState.score_duration;
    const gameEnd = startMs + openLobbyState.game_duration * 1000;
    const roundEnd = gameEnd + scoreDuration * 1000;

    const tick = () => {
      const now = Date.now();
      setGameRemaining(Math.max(0, Math.round((gameEnd - now) / 1000)));
      setScoreCountdown(Math.max(0, Math.round((roundEnd - now) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    openLobbyState?.start_time,
    openLobbyState?.game_duration,
    openLobbyState?.score_duration,
  ]);

  useEffect(() => {
    let isCancelled = false;
    const daysAgo = activeDate === "today" ? 0 : 1;

    void Promise.resolve().then(() => {
      if (!isCancelled) setIsLoading(true);
    });

    api
      .getScoreboard(gameLanguage, daysAgo)
      .then((data) => {
        if (isCancelled) return;

        setScoreboard(data.scores || []);
        const entries = (data.scores || [])
          .slice(0, 10)
          .map((s: ScoreEntry) => ({
            name: s.player_name,
            image: s.player_image,
            provider: s.player_provider,
            time: s.completion_time_seconds,
            clicks: s.moves.length,
            meta: `${s.found_count}/${s.target_count}`,
          }));
        setLeaderboard(entries);
      })
      .catch(() => {
        if (isCancelled) return;

        setScoreboard([]);
        setLeaderboard([]);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [gameLanguage, activeDate]);

  const handleCreateLobby = () => {
    void createLobby();
  };

  const createLobby = async () => {
    trackEvent("create-lobby-hero");
    setIsCreating(true);
    api
      .createLobby(gameLanguage)
      .then((id) => {
        setIsCreating(false);
        navigate(`/wikiparty/${id}`);
      })
      .catch((err) => {
        setCreateError(
          err instanceof Error ? err.message : t("home.errors.createLobby"),
        );
        setIsCreating(false);
      });
  };

  const handleJoinLobby = () => {
    if (lobbyCode) {
      const code = lobbyCode.match(/wikiparty\/(.+)/)?.[1] || lobbyCode;
      trackEvent("join-lobby-hero", { lobbyCode: code });
      navigate(`/wikiparty/${code}`);
    }
  };

  const handleDailyChallenge = () => {
    trackEvent("daily-challenge-hero");
    navigate("/daily");
  };

  const handleDuels = () => {
    trackEvent("duels-hero");
    navigate("/duels");
  };

  const handlePlayOpenLobby = () => {
    if (openLobbyState?.phase !== "Game") return;
    const lobbyId = openLobbyState.id;
    trackEvent("openlobby-player-start", { lobbyId });
    navigate(`/openlobby/${lobbyId}/game`);
  };

  const timelinePlayers =
    scoreboard?.map((score) => ({
      name: score.player_name,
      articles: score.moves.map((move) => ({
        text: move.title,
        duration: move.duration_seconds,
        type: move.is_target ? ("highlight" as const) : undefined,
      })),
    })) || [];

  const twitchEmbedUrl = streamer
    ? `https://player.twitch.tv/?channel=${encodeURIComponent(streamer)}&parent=${encodeURIComponent(window.location.hostname)}&muted=true`
    : null;

  if (isCreating) {
    return (
      <GameLoadingScreen />
    );
  }

  if (createError) {
    return (
      <div
        className={`min-h-screen ${theme.bg} flex items-center justify-center p-6`}
      >
        <div className="text-center space-y-4">
          <div
            className={`w-16 h-16 rounded-2xl ${theme.errorBg} flex items-center justify-center mx-auto`}
          >
            <svg
              className={`w-8 h-8 ${theme.errorText}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <p className={`${theme.text} font-medium`}>
            {t("home.errors.createChallenge")}
          </p>
          <p className={`${theme.textMuted} text-sm`}>{createError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* Header */}
      <header className="w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <HomeLink />
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <AdminLink />
              <AccountLink />
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
            </div>
          </div>
        </div>
      </header>

      <main className="pb-24">
        <h1 className="sr-only">{t("home.hero.title")}</h1>
        <BoardHero
          isDark={isDark}
          lang={lang}
          openLobby={boardOpenLobby}
          openLobbyAvailable={features.openLobby && openLobbyConnected}
          onPlay={handlePlayOpenLobby}
          onDaily={handleDailyChallenge}
          onDuel={features.duels ? handleDuels : undefined}
          onParty={handleCreateLobby}
          lobbyCode={lobbyCode}
          onLobbyCodeChange={setLobbyCode}
          onJoin={handleJoinLobby}
        />
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <div className="mt-12 max-w-4xl mx-auto space-y-3">
              <h3
                className={`text-md font-medium tracking-widest uppercase ${theme.textMuted}`}
              >
                {t("home.stream.currentlyStreaming")}
              </h3>
              {twitchEmbedUrl ? (
                <div
                  className={`overflow-hidden rounded-xl border ${theme.border} ${theme.bgSecondary}`}
                >
                  <iframe
                    src={twitchEmbedUrl}
                    title={`${streamer} on Twitch`}
                    allowFullScreen
                    className="block w-full aspect-video"
                  />
                </div>
              ) : (
                <a
                  href="https://www.twitch.tv/directory/category/wikiparty-org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center py-12 px-6 cursor-pointer ${theme.panel} ${theme.cardHover}`}
                >
                  <span className={`text-sm ${theme.textMuted}`}>
                    {t("home.stream.noStreamer")}
                  </span>
                </a>
              )}
            </div>
          </div>

          {/* Leaderboard Section */}
          <div className="max-w-3xl mx-auto">
            {/* Date Selector */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className={`${theme.text} text-xl font-medium`}>
                {t("home.leaderboard.title")}
              </h2>
              <TabGroup theme={theme}>
                <TabButton
                  active={activeDate === "today"}
                  onClick={() => setActiveDate("today")}
                  isDark={isDark}
                  theme={theme}
                >
                  {t("home.leaderboard.today")}
                </TabButton>
                <TabButton
                  active={activeDate === "yesterday"}
                  onClick={() => {
                    setActiveDate("yesterday");
                    setActiveView("leaderboard");
                  }}
                  isDark={isDark}
                  theme={theme}
                >
                  {t("home.leaderboard.yesterday")}
                </TabButton>
              </TabGroup>
            </div>

            {/* View Tabs (only for yesterday) */}
            {activeDate === "yesterday" && (
              <TabGroup theme={theme} className="mb-6">
                <TabButton
                  active={activeView === "leaderboard"}
                  onClick={() => setActiveView("leaderboard")}
                  isDark={isDark}
                  theme={theme}
                  className="flex-1"
                >
                  {t("home.leaderboard.tabs.leaderboard")}
                </TabButton>
                <TabButton
                  active={activeView === "paths"}
                  onClick={() => setActiveView("paths")}
                  isDark={isDark}
                  theme={theme}
                  className="flex-1"
                >
                  {t("home.leaderboard.tabs.paths")}
                </TabButton>
              </TabGroup>
            )}

            {/* Content */}
            {isLoading ? (
              <div className="flex justify-center py-16">
                <Pawn hopping className="h-10 w-10" />
              </div>
            ) : activeDate === "yesterday" && activeView === "paths" ? (
              // Player Paths View (Yesterday only)
              <div
                className={`rounded-2xl ${theme.sectionBorder} ${theme.sectionBg} p-6`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.bgSecondary} ${theme.border} border`}
                  >
                    <SvgIcon
                      markup={mapIcon}
                      size={16}
                      className={`${theme.text}`}
                    />
                  </div>
                  <h3
                    className={`text-sm font-semibold uppercase tracking-wider ${theme.textMuted}`}
                  >
                    {t("home.leaderboard.howEveryonePlayed")}
                  </h3>
                </div>

                {timelinePlayers.length > 0 ? (
                  <Timeline players={timelinePlayers} isDark={isDark} />
                ) : (
                  <div className={`text-center py-16 ${theme.textMuted}`}>
                    <div
                      className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${theme.bgSecondary} flex items-center justify-center`}
                    >
                      <SvgIcon
                        markup={mapIcon}
                        size={32}
                        className={`${theme.textMuted}`}
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className="text-sm">
                      {t("home.leaderboard.noPathData")}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Leaderboard View
              <div
                className={`rounded-2xl ${theme.sectionBorder} ${theme.sectionBg} overflow-hidden`}
              >
                <Leaderboard
                  entries={leaderboard}
                  isDark={isDark}
                  theme={theme}
                  maxItems={10}
                />
              </div>
            )}

            <p className={`text-center ${theme.textMuted} text-sm mt-4`}>
              {activeDate === "today"
                ? t("home.leaderboard.todaysResults")
                : t("home.leaderboard.yesterdaysResults")}
            </p>
          </div>

          {/* Game Modes Section */}
          <div className="max-w-4xl mx-auto mt-24">
            <h2
              className={`text-4xl ${theme.text} text-center mb-12`}
            >
              {t("home.howToPlay.title")}
            </h2>
            <div
              className={`grid gap-6 ${features.duels ? "md:grid-cols-3" : "md:grid-cols-2"}`}
            >
              <ModeRulesCard
                mode="party"
                isDark={isDark}
                title={t("home.howToPlay.multiplayer.title")}
                steps={RULE_STEPS.map((i) =>
                  t(`home.howToPlay.multiplayer.steps.${i}`),
                )}
              />
              <ModeRulesCard
                mode="daily"
                isDark={isDark}
                title={t("home.howToPlay.daily.title")}
                steps={RULE_STEPS.map((i) => t(`home.howToPlay.daily.steps.${i}`))}
              />
              {features.duels && (
                <ModeRulesCard
                  mode="duel"
                  isDark={isDark}
                  title={t("home.howToPlay.duels.title")}
                  steps={RULE_STEPS.map((i) =>
                    t(`home.howToPlay.duels.steps.${i}`),
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className={`border-t ${theme.border} px-4 sm:px-6 lg:px-8 py-8`}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p
              className={`${theme.textMuted} text-sm text-center sm:text-left`}
            >
              {t("home.footer.feedback")}{" "}
              <a
                href="mailto:info@wikirace.gg"
                className={`${theme.text} font-medium`}
              >
                info@wikirace.gg
              </a>{" "}
              {t("home.footer.orDiscord")}
            </p>
            <DiscordButton buttonClassName="w-auto px-4 py-2 text-sm rounded-lg" />
          </div>
          <nav
            aria-label="wikirace.gg pages"
            className={`flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs ${theme.textMuted} sm:justify-start`}
          >
            <a href="/wiki-race/" className={footerLinkClassName}>
              Wiki Race
            </a>
            <a href="/multiplayer/" className={footerLinkClassName}>
              Multiplayer
            </a>
            <a href="/daily-puzzle-game/" className={footerLinkClassName}>
              Daily Puzzle Game
            </a>
            <a
              href="/the-wiki-game-alternative/"
              className={footerLinkClassName}
            >
              The Wiki Game Alternative
            </a>
            <a href="/wikipedia-game/" className={footerLinkClassName}>
              Wikipedia Game
            </a>
            <a href="/wikipedia-speedrun/" className={footerLinkClassName}>
              Wikipedia Speedrun
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
